import {
  type AccountListEntry,
  type BucketEntry,
  accountsList,
  bucketsList,
} from '@chorepop/core/client';
import { type Component, For, Show, createMemo, createResource } from 'solid-js';

type Props = {
  account: AccountListEntry;
  sessionId: string;
  currencyCode: string;
  onSelectChild: (childAccountId: number, childDisplayName: string) => void;
  onOpenBuckets: (childAccountId: number, childDisplayName: string, buckets: BucketEntry[]) => void;
  onOpenChores: (childAccountId: number, childDisplayName: string) => void;
  onOpenMoney: (childAccountId: number, childDisplayName: string) => void;
  onAddChild: () => void;
};

function formatBalance(minorUnits: number, currencyCode: string): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currencyCode,
  }).format(minorUnits / 100);
}

export const GuardianKidsScreen: Component<Props> = (props) => {
  const [accounts] = createResource(async () => {
    const result = await accountsList();
    return result.ok ? result.value.filter((entry) => entry.roleCode === 'child') : [];
  });
  const [buckets] = createResource(async () => {
    const result = await bucketsList({ sessionId: props.sessionId });
    return result.ok ? result.value : [];
  });

  const childRows = createMemo(() => {
    const bucketRows = buckets() ?? [];
    return (accounts() ?? []).map((child) => {
      const childBuckets = bucketRows.filter((bucket) => bucket.childAccountId === child.accountId);
      return {
        child,
        buckets: childBuckets,
        total: childBuckets.reduce((sum, bucket) => sum + bucket.balanceMinorUnits, 0),
      };
    });
  });

  return (
    <main class="guardian-kids-screen">
      <header class="guardian-kids-header">
        <div>
          <span class="mobile-home-eyebrow">{childRows().length} children</span>
          <h1>Kids</h1>
          <p>See each child’s balance, then jump straight to what you need.</p>
        </div>
        <button
          type="button"
          class="primary guardian-kids-add"
          onClick={() => props.onAddChild()}
          data-testid="mobile-kids-add-child"
        >
          + Add family member
        </button>
      </header>

      <section class="guardian-kids-list" aria-label="Child accounts">
        <Show
          when={!accounts.loading}
          fallback={<p class="guardian-kids-empty">Loading children…</p>}
        >
          <Show
            when={childRows().length > 0}
            fallback={<p class="guardian-kids-empty">No child accounts yet.</p>}
          >
            <For each={childRows()}>
              {(row) => (
                <article
                  class="guardian-kids-card"
                  data-testid={`mobile-kids-child-${row.child.accountId}`}
                >
                  <button
                    type="button"
                    class="guardian-kids-card-summary"
                    onClick={() => props.onSelectChild(row.child.accountId, row.child.displayName)}
                    aria-label={`Open ${row.child.displayName}'s account`}
                  >
                    <span class="guardian-kids-avatar" aria-hidden="true">
                      {row.child.displayName.slice(0, 1).toUpperCase()}
                    </span>
                    <span class="guardian-kids-card-copy">
                      <strong>{row.child.displayName}</strong>
                      <small>{row.buckets.length} buckets · Tap to manage</small>
                      <span class="guardian-child-split-bar" aria-label="Bucket split">
                        <For each={row.buckets as BucketEntry[]}>
                          {(bucket) => (
                            <span
                              style={`--bucket-share: ${Math.max(bucket.allocationBps, 1)}; --bucket-color: ${bucket.displayColor ?? 'var(--brand)'}`}
                              title={`${bucket.bucketName} ${Math.round(bucket.allocationBps / 100)}%`}
                            />
                          )}
                        </For>
                      </span>
                    </span>
                    <span class="guardian-kids-balance">
                      {formatBalance(row.total, props.currencyCode)}
                    </span>
                    <span class="guardian-kids-chevron" aria-hidden="true">
                      ›
                    </span>
                  </button>
                  <div
                    class="guardian-kids-actions"
                    aria-label={`Shortcuts for ${row.child.displayName}`}
                  >
                    <button
                      type="button"
                      onClick={() =>
                        props.onOpenBuckets(row.child.accountId, row.child.displayName, row.buckets)
                      }
                    >
                      Buckets
                    </button>
                    <button
                      type="button"
                      onClick={() => props.onOpenChores(row.child.accountId, row.child.displayName)}
                    >
                      Chores
                    </button>
                    <button
                      type="button"
                      onClick={() => props.onOpenMoney(row.child.accountId, row.child.displayName)}
                    >
                      Money
                    </button>
                  </div>
                </article>
              )}
            </For>
          </Show>
        </Show>
      </section>
    </main>
  );
};
